import { useCallback } from 'react';

import type {
  DownloadManifest,
  DownloadSource
} from '../contexts/downloads.context';
import { assertNever } from '../helpers/exhaustiveness.helper';
import { useResolveAuditLogDownloadUrl } from './use-audit-log-download.hooks';
import { useResolveDataTableDownloadUrl } from './use-data-table-download.hooks';

type ResolveDownloadUrl = (
  manifest: DownloadManifest,
  onSourceResolved: (source: DownloadSource) => void,
  signal?: AbortSignal
) => Promise<string>;

/**
 * Turns a manifest's serializable {@link DownloadSource} into a URL, starting
 * or resuming the server-side job behind it as needed.
 *
 * `onSourceResolved` reports a source that has learned its server job id or
 * pickup time, so it can be persisted mid-flight and a reload can resume rather
 * than restart.
 */
export const useResolveDownloadUrl = (): ResolveDownloadUrl => {
  const resolveDataTableUrl = useResolveDataTableDownloadUrl();
  const resolveAuditLogUrl = useResolveAuditLogDownloadUrl();

  return useCallback(
    (manifest, onSourceResolved, signal) => {
      const { source, startTimestamp } = manifest;

      // Legacy rows rehydrate to FAILURE so this is unreachable today, but
      // `strict: false` would let a future caller NPE on `source.type`.
      if (!source) {
        return Promise.reject(
          new Error('Interrupted — start the download again')
        );
      }

      switch (source.type) {
        case 'STATIC':
          return Promise.resolve(source.sourcePath);
        // Never default `startTimestamp`: a resumed job would get a fresh poll
        // window, which is the unbounded polling this was written to stop.
        case 'DATA_TABLE':
          return resolveDataTableUrl(
            source,
            startTimestamp,
            onSourceResolved,
            signal
          );
        case 'AUDIT_LOG':
          return resolveAuditLogUrl(
            source,
            startTimestamp,
            onSourceResolved,
            signal
          );
        default:
          assertNever(source);
          return Promise.reject(new Error('Unsupported download source'));
      }
    },
    [resolveDataTableUrl, resolveAuditLogUrl]
  );
};
